import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

import api, { isNativeApp } from '../api/axios';
import { rememberedAuctions } from '../offline/credentials';
import { applyBrowserBranding } from '../utils/browserBranding';
import { logoUrl } from '../utils/urls';

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [companyLogo, setCompanyLogo] = useState('/lazer.png');
  const [companyName, setCompanyName] = useState(process.env.REACT_APP_COMPANY_NAME || 'Lazer');
  const [isMobile, setIsMobile] = useState(false);
  const [auctionOptions, setAuctionOptions] = useState([]);
  const [selectedAuctionId, setSelectedAuctionId] = useState('');

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 1024);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    api.get('/settings/company').then(({ data }) => {
      setCompanyLogo(logoUrl(data?.company_logo_path));
      setCompanyName(data?.company_name || process.env.REACT_APP_COMPANY_NAME || 'Lazer');
    }).catch(() => {
      setCompanyLogo('/lazer.png');
      setCompanyName(process.env.REACT_APP_COMPANY_NAME || 'Lazer');
    });
  }, []);

  useEffect(() => {
    applyBrowserBranding({
      companyName,
      logoUrl: companyLogo,
      pageLabel: 'تسجيل الدخول',
    });
  }, [companyName, companyLogo]);

  useEffect(() => {
    const trimmedUsername = String(username || '').trim();
    if (!trimmedUsername) {
      setAuctionOptions([]);
      setSelectedAuctionId('');
      return;
    }

    let cancelled = false;
    const timer = window.setTimeout(async () => {
      try {
        const { data } = await api.post('/auth/login-auctions', { username: trimmedUsername });
        if (cancelled) return;
        const rows = Array.isArray(data?.auctions) ? data.auctions : [];
        setAuctionOptions(rows);
        setSelectedAuctionId((current) => {
          if (current && rows.some((auction) => String(auction.id) === String(current))) return current;
          return rows[0]?.id ? String(rows[0].id) : '';
        });
      } catch (err) {
        if (cancelled) return;
        // دون اتصال: المزادات التي دخلها المستخدم سابقًا على هذا الجهاز.
        const rows = !err?.response && isNativeApp() ? await rememberedAuctions(trimmedUsername) : [];
        if (cancelled) return;
        setAuctionOptions(rows);
        setSelectedAuctionId(rows.length === 1 ? String(rows[0].id) : '');
      }
    }, 350);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [username]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(username, password, selectedAuctionId || null);
      navigate('/');
    } catch (err) {
      if (err?.response?.status === 401 || err?.invalidCredentials) {
        setError('بيانات الدخول غير صحيحة');
      } else if (!err?.response && !err?.isAxiosError && err?.message) {
        // رسالة الدخول دون اتصال (لم يدخل من قبل على هذا الجهاز، أو المزاد غير محفوظ).
        setError(err.message);
      } else if (err?.response?.status === 419) {
        setError('انتهت/فشلت جلسة CSRF (419). قم بتحديث الصفحة تحديثًا قويًا Ctrl+F5 ثم جرّب مجددًا.');
      } else {
        const serverMessage = typeof err?.response?.data?.message === 'string'
          ? err.response.data.message
          : '';
        const details = serverMessage || err?.message ? ` | ${serverMessage || err.message}` : '';
        setError(`تعذر الاتصال بالخادم.${details}`);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        backgroundColor: '#f3f4f6',
        padding: '2rem 1rem',
        backgroundImage: `radial-gradient(circle at top left, rgba(184, 15, 26, 0.14), transparent 30%),url(${companyLogo})`,
        backgroundRepeat: 'no-repeat, no-repeat',
        backgroundSize: 'auto, 220px auto',
        backgroundPosition: 'top left, right 40px bottom 40px',
      }}
    >
      <div
        style={{
          margin: 'auto',
          minHeight: 'calc(100vh - 3rem)',
          maxWidth: '96rem',
          overflow: 'hidden',
          borderRadius: '1.5rem',
          border: '1px solid #e2e8f0',
          background: '#fff',
          boxShadow: '0 8px 32px 0 rgba(31, 41, 55, 0.18)',
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '1.05fr 0.95fr',
        }}
      >
        <div
          style={{
            position: 'relative',
            overflow: 'hidden',
            background: 'linear-gradient(135deg, #09142e 0%, #000000 58%, #b80f1a 100%)',
            color: '#fff',
            padding: '2.5rem',
            flexDirection: 'column',
            justifyContent: 'space-between',
            display: isMobile ? 'none' : 'flex',
          }}
        >
          <div
            style={{
              position: 'absolute',
              inset: 0,
              backgroundImage:
                'radial-gradient(circle at top right,rgba(255,255,255,0.16),transparent 35%),radial-gradient(circle at bottom left,rgba(255,255,255,0.12),transparent 30%)',
            }}
          />
          <div style={{ position: 'relative', zIndex: 1 }}>
            <h2 style={{ fontSize: '2.5rem', fontWeight: 800, marginBottom: '1rem' }}>مرحبا بعودتك</h2>
            <p style={{ maxWidth: '26rem', lineHeight: 1.8, color: '#cbd5e1' }}>
              قم بتسجيل الدخول إلى نظام الفحص الخاص بك للوصول إلى التقارير وإدارة البيانات بسهولة وأمان.
            </p>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: isMobile ? '1.5rem' : '2.5rem' }}>
          <form onSubmit={onSubmit} style={{ width: '100%', maxWidth: '30rem' }}>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1.5rem' }}>
              <img
                src={companyLogo}
                alt="شعار الشركة"
                style={{ width: '9rem', maxHeight: '7rem', height: 'auto', objectFit: 'contain' }}
              />
            </div>
            <h2 style={{ fontSize: '1.875rem', fontWeight: 800, textAlign: 'center', color: '#111827', marginBottom: '0.5rem' }}>نظام فحص السيارات</h2>
            <p style={{ fontSize: '1rem', textAlign: 'center', color: '#64748b', marginBottom: '1rem' }}>سجّل دخولك واختر المزاد المتاح لك قبل المتابعة.</p>
            <div style={{ borderRadius: '1rem', border: '1px solid #e2e8f0', background: '#fff', padding: '1.5rem', boxShadow: '0 1px 4px 0 rgba(31, 41, 55, 0.08)', marginBottom: '1.5rem' }}>
              <input
                type="text"
                placeholder="اسم المستخدم"
                style={{ width: '100%', borderRadius: '0.75rem', border: '1px solid #cbd5e1', padding: '0.9rem 1rem', fontSize: '1rem', outline: 'none', transition: 'border-color 0.2s', marginBottom: '0.9rem', boxSizing: 'border-box' }}
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
              />
              <select
                style={{ width: '100%', borderRadius: '0.75rem', border: '1px solid #cbd5e1', padding: '0.9rem 1rem', fontSize: '1rem', outline: 'none', transition: 'border-color 0.2s', marginBottom: '0.9rem', boxSizing: 'border-box' }}
                value={selectedAuctionId}
                onChange={(e) => setSelectedAuctionId(e.target.value)}
                disabled={!auctionOptions.length}
                required={auctionOptions.length > 0}
              >
                <option value="">{auctionOptions.length ? 'اختر المزاد' : 'أدخل اسم المستخدم أولاً'}</option>
                {auctionOptions.map((auction) => (
                  <option key={auction.id} value={auction.id}>{auction.name}</option>
                ))}
              </select>
              <input
                type="password"
                placeholder="كلمة المرور"
                style={{ width: '100%', borderRadius: '0.75rem', border: '1px solid #cbd5e1', padding: '0.9rem 1rem', fontSize: '1rem', outline: 'none', transition: 'border-color 0.2s', marginBottom: '0.9rem', boxSizing: 'border-box' }}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />

              {error && <p style={{ color: '#dc2626', fontSize: '0.95rem', marginBottom: '0.5rem', textAlign: 'center' }}>{error}</p>}
              <button
                type="submit"
                disabled={loading}
                style={{ width: '100%', borderRadius: '0.75rem', background: '#b80f1a', padding: '0.95rem 1rem', color: '#fff', fontWeight: 700, fontSize: '1rem', transition: 'opacity 0.2s', cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.65 : 1 }}
              >
                {loading ? 'جاري المعالجة...' : 'دخول'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
